<script setup lang="ts">
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronDown, faChevronRight, faTimes } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
library.add(faChevronDown, faChevronRight, faTimes)

interface PriceTier {
    min_quantity: number
    price: number
}

const props = withDefaults(defineProps<{
    title: string
    titleSuffix?: string
    subtitle?: string
    meta?: string
    summary?: string
    catalogTiers: PriceTier[]
    tiers?: PriceTier[]
    picked: boolean
    expanded?: boolean
    expandable?: boolean
}>(), {
    expanded: false,
    expandable: true,
})

const emits = defineEmits<{
    (e: 'toggle'): void
    (e: 'reset'): void
    (e: 'toggleExpand'): void
}>()

const basePrice = (tiers?: PriceTier[]) => tiers?.length ? tiers[0].price : 0

const catalogBasePrice = computed(() => basePrice(props.catalogTiers))
const effectiveBasePrice = computed(() => props.picked ? basePrice(props.tiers) : catalogBasePrice.value)

const catalogPriceFor = (minQuantity: number) => props.catalogTiers
    .find(tier => tier.min_quantity === minQuantity)?.price

const isTierChanged = (tier: PriceTier) => {
    const catalogPrice = catalogPriceFor(tier.min_quantity)

    return catalogPrice !== undefined && catalogPrice !== tier.price
}

const isChanged = computed(() => JSON.stringify(props.tiers ?? []) !== JSON.stringify(props.catalogTiers))

const addTier = () => {
    const lastTier = props.tiers?.[props.tiers.length - 1]

    props.tiers?.push({
        min_quantity : (lastTier?.min_quantity ?? 0) + 100,
        price        : lastTier?.price ?? 0,
    })
}

const removeTier = (index: number) => props.tiers?.splice(index, 1)
</script>

<template>
    <div class="rounded-lg" :class="picked ? 'bg-indigo-50/60' : ''">
        <div class="flex items-start">
            <button
                v-if="expandable"
                type="button"
                class="mt-1 shrink-0 px-2 py-2 text-gray-400 hover:text-gray-600"
                :aria-expanded="expanded"
                v-tooltip="expanded ? 'Tutup' : 'Lihat varian / atur harga brand'"
                @click="emits('toggleExpand')"
            >
                <FontAwesomeIcon :icon="expanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'" fixed-width aria-hidden="true" />
            </button>
            <span v-else class="mt-1 shrink-0 px-2 py-2" aria-hidden="true">
                <span class="inline-block w-[1.25em]" />
            </span>

            <label class="flex min-w-0 flex-1 cursor-pointer items-start gap-3 py-2 pr-2">
                <input
                    type="checkbox"
                    :checked="picked"
                    class="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    @change="emits('toggle')"
                />

                <div class="min-w-0 flex-1">
                    <div class="text-sm font-medium text-gray-800">
                        {{ title }}
                        <span v-if="titleSuffix" class="text-xs font-normal text-gray-400">({{ titleSuffix }})</span>
                    </div>
                    <div v-if="subtitle" class="truncate font-mono text-xs text-gray-400">{{ subtitle }}</div>
                    <div v-if="meta" class="truncate text-xs text-gray-500">{{ meta }}</div>
                </div>

                <div class="shrink-0 text-right text-sm leading-tight">
                    <div
                        v-if="isChanged && effectiveBasePrice !== catalogBasePrice"
                        class="line-through"
                        :class="effectiveBasePrice > catalogBasePrice ? 'text-red-500' : 'text-green-600'"
                    >
                        {{ useFormatCurrency(catalogBasePrice) }}
                    </div>
                    <div :class="isChanged ? 'font-semibold text-gray-800' : 'text-gray-600'">
                        {{ useFormatCurrency(effectiveBasePrice) }}
                    </div>
                    <div v-if="catalogTiers.length > 1" class="text-xs text-gray-400">{{ catalogTiers.length }} tingkat qty</div>
                </div>
            </label>
        </div>

        <button
            v-if="summary && !expanded"
            type="button"
            class="pb-2 pl-10 text-xs text-indigo-500 hover:underline"
            @click="emits('toggleExpand')"
        >
            {{ summary }}
        </button>

        <div v-if="picked && tiers && expanded" class="flex flex-col gap-2 px-2 pb-3 pl-10">
            <div v-for="(tier, index) in tiers" :key="`tier-${index}`" class="flex flex-wrap items-center gap-2">
                <span class="text-xs text-gray-500">Qty &ge;</span>
                <input
                    v-model.number="tier.min_quantity"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    class="w-20 rounded-md border-gray-300 text-sm"
                />
                <span class="text-xs text-gray-500">Rp</span>
                <input
                    v-model.number="tier.price"
                    type="number"
                    min="0"
                    inputmode="numeric"
                    class="w-28 flex-1 rounded-md border-gray-300 text-sm"
                />

                <span
                    v-if="isTierChanged(tier)"
                    class="text-xs line-through"
                    :class="tier.price > (catalogPriceFor(tier.min_quantity) ?? 0) ? 'text-red-500' : 'text-green-600'"
                >
                    {{ useFormatCurrency(catalogPriceFor(tier.min_quantity)) }}
                </span>

                <button
                    v-if="tiers.length > 1"
                    type="button"
                    class="px-1 text-gray-400 hover:text-red-500"
                    @click="removeTier(index)"
                >
                    <FontAwesomeIcon icon="fal fa-times" fixed-width aria-hidden="true" />
                </button>
            </div>

            <div class="flex flex-wrap items-center gap-x-4 gap-y-1">
                <button type="button" class="text-xs text-indigo-600 hover:underline" @click="addTier">
                    + Tambah tingkat qty
                </button>
                <button v-if="isChanged" type="button" class="text-xs text-gray-500 hover:underline" @click="emits('reset')">
                    Kembalikan harga katalog
                </button>
            </div>
        </div>
    </div>
</template>
